import { isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { isCountryCode, type CountryCode } from './countries';

// `/min`, not the full build: this package rides inside every web bundle and the
// native app, and the full metadata is an order of magnitude larger. `min` cannot
// format nationally or tell mobile from landline — neither of which is needed to
// decide whether a string is a real number and turn it into E.164.

/** Fixed width, so the mask does not leak how long the number is. */
const MASK = '•••••';

const VISIBLE_DIGITS = 4;

/**
 * The one place a typed phone number becomes the stored form. NO default country
 * is baked in — the backend passes none and a client passes the dial-code
 * select's country, because a silent default turns a foreign number into a local
 * one that validates.
 */
export function normalizePhoneE164(raw: string, defaultCountry?: CountryCode): string | null {
    if (typeof raw !== 'string') return null;

    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;

    // Our CountryCode covers all 249 ISO entries; libphonenumber knows fewer, and
    // an unknown one must not be passed through as a hint.
    const country =
        defaultCountry !== undefined && isSupportedCountry(defaultCountry)
            ? defaultCountry
            : undefined;

    const parsed = parsePhoneNumberFromString(trimmed, country);
    if (!parsed || !parsed.isValid()) return null;

    return parsed.number;
}

/** Already-E.164 check: it must carry its country code and survive a round trip. */
export function isValidPhoneE164(value: string): boolean {
    if (typeof value !== 'string' || !value.startsWith('+')) return false;

    return normalizePhoneE164(value) === value;
}

/** The halves a dial-code picker plus a national-number field hold. */
export interface PhoneE164Parts {
    /**
     * ISO 3166-1 alpha-2, or `null` when the metadata names none. It is the
     * metadata's answer, NOT the caller's: `+44` resolves to `GG` (Guernsey),
     * which `DIAL_CODES` does not list — resolve a picker row by `callingCode`
     * when `country` matches no row, or a pasted number loses its code.
     */
    country: CountryCode | null;
    /** Digits after the `+`, without the national number (`'44'`, `'20'`, `'1'`). */
    callingCode: string;
    /** The number without its calling code, digits only. */
    national: string;
}

/**
 * Split a stored number back into the two fields a picker renders. The inverse of
 * `normalizePhoneE164`, so a pasted `+…` can re-select the country rather than
 * being validated against whatever the select happened to show.
 */
export function splitPhoneE164(value: string): PhoneE164Parts | null {
    if (typeof value !== 'string' || value.trim() === '') return null;

    const parsed = parsePhoneNumberFromString(value.trim());
    if (!parsed) return null;

    const country = parsed.country;

    return {
        country: country !== undefined && isCountryCode(country) ? country : null,
        callingCode: parsed.countryCallingCode,
        national: parsed.nationalNumber,
    };
}

/** `'+201234567890'` → `'+20•••••7890'`. For display and for logs. */
export function maskPhoneE164(value: string): string {
    if (typeof value !== 'string') return MASK;

    const parsed = parsePhoneNumberFromString(value);
    const prefix = parsed ? `+${parsed.countryCallingCode}` : '+';
    const digits = value.replace(/\D/g, '');

    return `${prefix}${MASK}${digits.slice(-VISIBLE_DIGITS)}`;
}
